import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Table, Text } from 'components';
import { Field, Form, Formik } from 'formik';
import { PageTitle } from 'pages/Layout/PageTitle';
import { tcomService } from 'services';

import { DialogConfirmApprove } from './Dialog/DialogConfirmApprove';
import { DialogFailureDiagnostics } from './Dialog/DialogFailureDiagnostics';
import { DialogTestDetails } from './Dialog/DialogTestDetails';
import { useStyles } from './styles';

const testColumns = [
  { label: 'Teste' },
  { label: 'Resultado' },
  { label: 'Observação' },
  { label: 'Detalhes', align: 'center' },
];

const logColumns = [{ label: 'Data' }, { label: 'Usuário' }, { label: 'Observação' }];

const historyColumns = [
  { label: 'Arquivo' },
  { label: 'Início do Teste' },
  { label: 'Operador' },
  { label: 'Resultado' },
];

const formatTcomDate = (value) => {
  if (!value) return '-';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}:${match[6]}`;
};

const formatLogDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('pt-BR');
};

// Enquanto testes com valor JSON (About Terminal, Mobile Network...) não têm classificação
// OK/NOK confiável (ver PLANO-TCOM.md), a defesa honesta é mostrar quantos foram de fato
// avaliados em vez de um banner genérico "todos os testes OK".
const summarizeTests = (tests) => {
  // Campos de identificação/módulo (informational: true) nunca foram um teste pass/fail — não
  // entram na contagem, senão "INNER SERIAL NUMBER" etc. aparecem como "teste não avaliado".
  const evaluable = (tests || []).filter((test) => !test.informational);
  const total = evaluable.length;
  const ok = evaluable.filter((test) => test.result === 'OK').length;
  const notEvaluated = evaluable.filter((test) => test.result === 'INFO').length;
  return { total, ok, notEvaluated };
};

export const LaudoTcom = () => {
  const classes = useStyles();
  const formikRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [data, setData] = useState(null);
  const [lastInput, setLastInput] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [detailsTest, setDetailsTest] = useState(null);
  const [openFailures, setOpenFailures] = useState(false);

  const fetchLaudo = async (input) => {
    setIsLoading(true);
    try {
      const { data: result } = await tcomService.getLaudoTcom({ input });

      if (result.status !== 200) {
        setData(null);
        toast.error(result.msg || 'Erro ao buscar o laudo TCoM.');
        return;
      }

      setData(result);
      setLastInput(input);

      if (result.laudo.failed) {
        toast.warning('Laudo com falha — aprovação bloqueada.');
        if (result.failureDiagnostics?.length > 0) {
          setOpenFailures(true);
        }
      } else if (result.laudo.incomplete) {
        toast.warning('Arquivo TCoM incompleto — aprovação bloqueada.');
      } else {
        toast.success('Laudo TCoM carregado.');
      }
    } catch (error) {
      setData(null);
      toast.error(error?.response?.data?.message || 'Erro ao buscar o laudo TCoM.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (values) => {
    const input = String(values.input_search || '').trim();
    if (!input) {
      toast.error('Informe a OS ou o serial.');
      return;
    }
    await fetchLaudo(input);
  };

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const { data: result } = await tcomService.approveLaudoTcom({
        numOs: data.os.numeroOs,
        fileName: data.laudo.fileName,
      });

      if (result.status === 200) {
        toast.success(result.msg);
        setOpenConfirm(false);
        window.location.reload();
      } else {
        toast.error(result.msg || 'Erro ao aprovar o laudo.');
        setOpenConfirm(false);
        // 409 = laudo mais novo ou OS mudou de etapa — recarrega para refletir o estado real
        if (result.status === 409) {
          await fetchLaudo(lastInput);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao aprovar o laudo.');
      setOpenConfirm(false);
    } finally {
      setIsApproving(false);
    }
  };

  const renderTestChip = (test) => {
    if (test.result === 'OK') return <Chip size='small' label='OK' className={classes.chipOk} />;
    if (test.result === 'NOK')
      return <Chip size='small' label='FALHA' className={classes.chipFail} />;
    return <Chip size='small' label='INFO' className={classes.chipInfo} />;
  };

  const getBanner = () => {
    if (!data) return null;
    const { failed, incomplete, failures } = data.laudo;

    if (failed) {
      let subtitle;
      if (data.alreadyInN2) {
        subtitle = 'A OS já se encontra em AGUARDANDO N2.';
      } else if (data.alreadyReproved) {
        subtitle =
          'Este arquivo já reprovou a OS anteriormente — nenhuma nova ação foi registrada.';
      } else {
        subtitle = 'Falha lançada como Informação Adicional na OS. A etapa não foi alterada.';
      }
      return {
        className: classes.bannerFail,
        icon: <CancelIcon className={classes.bannerIcon} />,
        title: `TESTE REPROVADO — ${failures.length} falha(s)`,
        subtitle,
      };
    }

    if (incomplete) {
      return {
        className: classes.bannerWarn,
        icon: <WarningIcon className={classes.bannerIcon} />,
        title: 'TESTE INCOMPLETO — arquivo sem resultado final',
        subtitle: 'Aprovação bloqueada. Reteste o terminal no TCoM.',
      };
    }

    let subtitle;
    if (data.alreadyImported) {
      subtitle = 'Este log do TCoM já foi importado anteriormente — não é possível duplicar.';
    } else if (data.alreadyInN2) {
      subtitle = 'A OS está em AGUARDANDO N2 — reteste necessário antes de aprovar.';
    } else {
      subtitle = 'A OS pode ser aprovada.';
    }
    const { total, ok, notEvaluated } = summarizeTests(data.laudo.tests);
    return {
      className: classes.bannerOk,
      icon: <CheckCircleIcon className={classes.bannerIcon} />,
      title:
        notEvaluated > 0
          ? `TESTE APROVADO — ${ok}/${total} teste(s) OK, ${notEvaluated} não avaliado(s)`
          : `TESTE APROVADO — ${ok}/${total} teste(s) OK`,
      subtitle,
    };
  };

  const banner = getBanner();

  const infoItems = data
    ? [
        { label: 'OS', value: data.os.numeroOs },
        { label: 'Serial', value: data.os.serial },
        { label: 'Etapa Atual', value: data.os.etapaDesc || data.os.idEtapa },
        { label: 'Product Number', value: data.laudo.productNumber },
        { label: 'Plano de Teste', value: data.laudo.testPlan },
        { label: 'Estágio', value: data.laudo.stage },
        { label: 'Bancada', value: data.laudo.workstation },
        { label: 'Início', value: formatTcomDate(data.laudo.startedTime) },
        { label: 'Fim', value: formatTcomDate(data.laudo.finalTime) },
        { label: 'Duração', value: data.laudo.duration },
        { label: 'Resultado TMA', value: data.laudo.tmaResult },
        { label: 'Resultado Final', value: data.laudo.finalResult },
      ]
    : [];

  return (
    <>
      <DialogConfirmApprove
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleApprove}
        isLoading={isApproving}
        data={data}
      />
      <DialogTestDetails
        open={Boolean(detailsTest)}
        onClose={() => setDetailsTest(null)}
        test={detailsTest}
      />
      <DialogFailureDiagnostics
        open={openFailures}
        onClose={() => setOpenFailures(false)}
        failures={data?.failureDiagnostics}
        numeroOs={data?.os?.numeroOs}
      />

      <PageTitle>Laudo TCoM</PageTitle>

      <Formik innerRef={formikRef} initialValues={{ input_search: '' }} onSubmit={handleSearch}>
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              <Field
                autoFocus
                innerRef={searchInputRef}
                size='small'
                name='input_search'
                label='OS ou Serial'
                variant='outlined'
                component={Text}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2}>
              <Button type='submit' fullWidth loading={isLoading}>
                Pesquisar
              </Button>
            </Grid>
            {data && (
              <Grid item xs={12} md={3} lg={2}>
                <Button
                  fullWidth
                  color='secondary'
                  variant='outlined'
                  onClick={() => {
                    setData(null);
                    formikRef.current?.resetForm();
                    searchInputRef.current?.focus();
                  }}
                >
                  Limpar
                </Button>
              </Grid>
            )}
          </Grid>
        </Form>
      </Formik>

      {data && (
        <>
          <Paper elevation={3} className={`${classes.banner} ${banner.className}`}>
            {banner.icon}
            <div>
              <Typography variant='h5' className={classes.bannerTitle}>
                {banner.title}
              </Typography>
              <Typography variant='body1'>{banner.subtitle}</Typography>
              <Typography variant='body2'>
                Operador TCoM: <strong>{data.laudo.user || '-'}</strong>{' '}
                {data.tcomUser?.matched
                  ? `(usuário XPCell: ${data.tcomUser.UCLogin})`
                  : '(sem vínculo com usuário XPCell)'}
              </Typography>
            </div>
          </Paper>

          {data.failureDiagnostics?.length > 0 && (
            <Paper elevation={0} className={classes.warningPaper}>
              <Typography variant='body2'>
                <strong>{data.failureDiagnostics.length} defeito(s) com DI</strong> foram lançados
                como Informação Adicional na OS.{' '}
                <Button
                  variant='text'
                  color='primary'
                  size='small'
                  onClick={() => setOpenFailures(true)}
                >
                  Ver detalhes
                </Button>
              </Typography>
            </Paper>
          )}

          {!data.tcomUser?.matched && data.laudo.user && (
            <Paper elevation={0} className={classes.warningPaper}>
              <Typography variant='body2'>
                O operador <strong>{data.laudo.user}</strong> do arquivo TCoM não foi localizado nos
                usuários do XPCell — o log foi/será registrado com o usuário logado.
              </Typography>
            </Paper>
          )}

          {summarizeTests(data.laudo.tests).notEvaluated > 0 && (
            <Paper elevation={0} className={classes.warningPaper}>
              <Typography variant='body2'>
                <strong>
                  {summarizeTests(data.laudo.tests).notEvaluated} teste(s) não avaliado(s)
                </strong>{' '}
                (resultado em formato que ainda não é classificado automaticamente):{' '}
                {data.laudo.tests
                  .filter((test) => test.result === 'INFO' && !test.informational)
                  .map((test) => test.name)
                  .join(', ')}
                . Confira os detalhes de cada um antes de aprovar.
              </Typography>
            </Paper>
          )}

          {data.laudo.parseWarnings?.length > 0 && (
            <Paper elevation={0} className={classes.warningPaper}>
              <Typography variant='body2' style={{ fontWeight: 600 }}>
                Avisos de leitura do arquivo ({data.laudo.parseWarnings.length}):
              </Typography>
              {data.laudo.parseWarnings.map((warning, index) => (
                <Typography variant='body2' key={`${index}-${warning}`}>
                  • {warning}
                </Typography>
              ))}
            </Paper>
          )}

          <Grid container spacing={2} style={{ marginTop: 8 }}>
            {infoItems.map((item) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={item.label}>
                <Paper variant='outlined' className={classes.infoCard}>
                  <Typography className={classes.infoLabel}>{item.label}</Typography>
                  <Typography className={classes.infoValue}>{item.value || '-'}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} style={{ marginTop: 16 }}>
            <Grid item xs={12} md={4} lg={3}>
              <Button
                fullWidth
                disabled={!data.canApprove}
                loading={isApproving}
                onClick={() => setOpenConfirm(true)}
              >
                Aprovar Laudo
              </Button>
            </Grid>
          </Grid>

          <Typography variant='h6' className={classes.sectionTitle}>
            Resultado dos Testes — {data.laudo.fileName}
          </Typography>
          <Table headers={testColumns} disableNumeration maxHeight loading={isLoading}>
            {data.laudo.tests.map((test) => (
              <TableRow key={`test-${test.order}`}>
                <TableCell>{test.name}</TableCell>
                <TableCell>{renderTestChip(test)}</TableCell>
                <TableCell>{test.cause || (test.result === 'NOK' ? test.raw : '-')}</TableCell>
                <TableCell align='center'>
                  {test.details ? (
                    <IconButton size='small' color='primary' onClick={() => setDetailsTest(test)}>
                      <VisibilityIcon fontSize='small' />
                    </IconButton>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))}
            {data.laudo.otherFailures?.map((failure) => (
              <TableRow key={`other-${failure.idItem}`}>
                <TableCell>{failure.coluna}</TableCell>
                <TableCell>
                  <Chip size='small' label='FALHA' className={classes.chipFail} />
                </TableCell>
                <TableCell>
                  {failure.falhaDI
                    ? `${failure.falhaDI} - ${failure.resultado}`
                    : failure.resultado}
                </TableCell>
                <TableCell align='center'>-</TableCell>
              </TableRow>
            ))}
          </Table>

          {data.history?.length > 0 && (
            <>
              <Typography variant='h6' className={classes.sectionTitle}>
                Testes Anteriores deste Serial
              </Typography>
              <Table headers={historyColumns} disableNumeration maxHeight>
                {data.history.map((item) => (
                  <TableRow key={item.fileName}>
                    <TableCell>{item.fileName}</TableCell>
                    <TableCell>{formatTcomDate(item.startedTime)}</TableCell>
                    <TableCell>{item.user}</TableCell>
                    <TableCell>
                      {item.failed ? (
                        <Chip size='small' label='FALHA' className={classes.chipFail} />
                      ) : (
                        <Chip size='small' label='OK' className={classes.chipOk} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </>
          )}

          {data.logs?.length > 0 && (
            <>
              <Typography variant='h6' className={classes.sectionTitle}>
                Histórico TCoM da OS (XPCell)
              </Typography>
              <Table headers={logColumns} disableNumeration maxHeight>
                {data.logs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatLogDate(log.dtCadastro)}</TableCell>
                    <TableCell>{log.UCUserName || '-'}</TableCell>
                    <TableCell>{log.obs}</TableCell>
                  </TableRow>
                ))}
              </Table>
            </>
          )}
        </>
      )}
    </>
  );
};
